import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';



import ExcelJS from 'exceljs';

// District-wise Reports Component with Excel Export and WhatsApp Sharing
const DistrictWiseReports = ({ onBack }) => {
 const [districtData, setDistrictData] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const [generatingPdf, setGeneratingPdf] = useState(false);
 const navigate = useNavigate();
 const reportRef = useRef(null);

 // Fetch district-wise utilised report data
 const fetchDistrictData = async () => {
   try {
     setLoading(true);
     setError(null);
     const authToken = localStorage.getItem('authToken');
     
     const response = await fetch(`https://crd-training.in/api/v1/ledger/report/utilised`, {
       method: 'GET',
       headers: {
         'Content-Type': 'application/json',
         'Authorization': authToken
       }
     });
     
     const responseData = await response.json();
     
     if (responseData.data && responseData.data.length > 0) {
       setDistrictData(responseData.data);
       setError(null);
     } else {
       setError(responseData.message || 'No data available');
       setDistrictData([]);
     }
   } catch (err) {
     setError('Network error: ' + err.message);
     console.error('Error:', err);
   } finally {
     setLoading(false);
   }
 };

 useEffect(() => {
   fetchDistrictData();
 }, []);

 // Clean work codes - removes semicolons and ensures proper comma formatting
 const cleanWorkCodes = (workcode) => {
   if (!workcode) return '';
   // Replace semicolons with commas, then clean up excess spaces and commas
   return workcode.replace(/;/g, ',')
     .split(',')
     .map(code => code.trim())
     .filter(code => code) // Remove empty entries
     .join(', ');
 };

// Calculate percentage of amount utilized (alloted - balance)/alloted * 100
const calculateUtilizationPercentage = (alloted, balance) => {
  const allotedAmount = parseFloat(alloted) || 0;
  if (allotedAmount === 0) return 0;
  
  const utilizedAmount = allotedAmount - parseFloat(balance);
  return Math.round((utilizedAmount / allotedAmount) * 100);
};

// Export All Centers to Excel - Updated to include all center types
const exportAllToExcel = async () => {
  try {
    if (districtData.length === 0) {
      alert('No data available to export');
      return;
    }

    // Group data by center type and sort each group
    const groupedData = districtData.reduce((acc, item) => {
      const centerType = item.center || 'Unknown';
      if (!acc[centerType]) {
        acc[centerType] = [];
      }
      acc[centerType].push(item);
      return acc;
    }, {});

    // Sort each group by utilization percentage (highest first)
    Object.keys(groupedData).forEach(centerType => {
      groupedData[centerType].sort((a, b) => {
        const percentageA = calculateUtilizationPercentage(a.alloted, a.balance);
        const percentageB = calculateUtilizationPercentage(b.alloted, b.balance);
        return percentageB - percentageA;
      });
    });

    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Fund Utilization Report');

    // Define column colors
    const columnColors = [
      'CCE5FF', // Light Blue - Center Type
      'E5CCFF', // Light Purple - S.No
      'CCFFCC', // Light Green - Center Name
      'FFFF99', // Light Yellow - Alloted Amount
      'FFCCDD', // Light Pink - Amount Utilised
      'CCDDFF', // Light Indigo - No. of Work Code Generated
      'FFDDCC', // Light Orange - Percentage of Amount Utilized
      'FFCCCC', // Light Red - Balance
    ];

    // Add title row
    worksheet.addRow(['RRGSA - Center Wise Fund Utilization Report (2025-26)']);
    worksheet.mergeCells('A1:H1');
    const titleCell = worksheet.getCell('A1');
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };

    // Add empty row
    worksheet.addRow([]);

    // Add headers
    const headers = [
      'Center Type',
      'S.No',
      'Center Name',
      'Alloted Amount',
      'Amount Utilised',
      'No. of Work Code Generated',
      'Percentage of Amount Utilized',
      'Balance'
    ];

    const headerRow = worksheet.addRow(headers);
    
    // Style header row with different colors for each column
    headerRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: 'FF' + columnColors[colNumber - 1] } 
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    // Add data rows
    Object.entries(groupedData).forEach(([centerType, centers]) => {
      centers.forEach((item, index) => {
        const allotedAmount = parseFloat(item.alloted) || 0;
        const balanceAmount = parseFloat(item.balance) || 0;
        const utilizedAmount = allotedAmount - balanceAmount;
        const utilizationPercentage = allotedAmount > 0 
          ? Math.round((utilizedAmount / allotedAmount) * 100) 
          : 0;
        
        const numCodes = item.workcode 
          ? item.workcode.replace(/;/g, ',').split(',').filter(code => code.trim()).length 
          : 0;

        const dataRow = worksheet.addRow([
          centerType,
          index + 1,
          item.name || '',
          Math.round(allotedAmount),
          Math.round(utilizedAmount),
          numCodes,
          utilizationPercentage,
          Math.round(balanceAmount)
        ]);

        // Apply column colors to each cell in the data row
        dataRow.eachCell((cell, colNumber) => {
          cell.fill = { 
            type: 'pattern', 
            pattern: 'solid', 
            fgColor: { argb: 'FF' + columnColors[colNumber - 1] } 
          };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

          // Set alignment based on column
          if (colNumber === 1 || colNumber === 2 || colNumber === 6 || colNumber === 7) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else if (colNumber === 4 || colNumber === 5 || colNumber === 8) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          } else {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
          }

          // Format percentage
          if (colNumber === 7) {
            cell.numFmt = '0"%"';
          }
          
          // Format currency
          if (colNumber === 4 || colNumber === 5 || colNumber === 8) {
            cell.numFmt = '₹#,##0';
          }
        });
      });
      
      // Add empty row between center types
      worksheet.addRow([]);
    });

    // Add summary section
    const summaryRow = worksheet.addRow(['SUMMARY']);
    worksheet.mergeCells(`A${summaryRow.number}:H${summaryRow.number}`);
    const summaryCell = worksheet.getCell(`A${summaryRow.number}`);
    summaryCell.font = { bold: true, size: 12 };
    summaryCell.alignment = { horizontal: 'center', vertical: 'middle' };
    summaryCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDDDDD' } };

    // Add summary data
    Object.entries(groupedData).forEach(([centerType, centers]) => {
      const typeTotal = centers.reduce((sum, item) => ({
        alloted: sum.alloted + (parseFloat(item.alloted) || 0),
        balance: sum.balance + (parseFloat(item.balance) || 0),
        works: sum.works + (parseInt(item.works) || 0)
      }), { alloted: 0, balance: 0, works: 0 });
      
      const typeUtilized = typeTotal.alloted - typeTotal.balance;
      const summaryDataRow = worksheet.addRow([
        `${centerType} Centers`,
        centers.length,
        `Total Alloted: ${Math.round(typeTotal.alloted)}`,
        `Total Utilized: ${Math.round(typeUtilized)}`,
        `Total Works: ${typeTotal.works}`,
        `Total Balance: ${Math.round(typeTotal.balance)}`,
        '',
        ''
      ]);

      // Style summary data rows
      summaryDataRow.eachCell((cell, colNumber) => {
        cell.fill = { 
          type: 'pattern', 
          pattern: 'solid', 
          fgColor: { argb: 'FFF0F0F0' } 
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    // Set column widths
    worksheet.columns = [
      { width: 15 }, // Center Type
      { width: 8 },  // S.No
      { width: 30 }, // Center Name
      { width: 15 }, // Alloted Amount
      { width: 15 }, // Amount Utilised
      { width: 20 }, // No. of Work Code Generated
      { width: 25 }, // Percentage of Amount Utilized
      { width: 15 }  // Balance
    ];

    // Generate filename with current date
    const filename = `All_Centers_Fund_Utilization_${new Date().toISOString().split('T')[0]}.xlsx`;

    // Write and download the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Error exporting data. Please try again: ' + error.message);
  }
};

// Alternative method using HTML table approach (if ExcelJS doesn't work)
const exportAllToExcelHTML = () => {
  try {
    if (districtData.length === 0) {
      alert('No data available to export');
      return;
    }

    // Group and sort data
    const groupedData = districtData.reduce((acc, item) => {
      const centerType = item.center || 'Unknown';
      if (!acc[centerType]) {
        acc[centerType] = [];
      }
      acc[centerType].push(item);
      return acc;
    }, {});

    Object.keys(groupedData).forEach(centerType => {
      groupedData[centerType].sort((a, b) => {
        const percentageA = calculateUtilizationPercentage(a.alloted, a.balance);
        const percentageB = calculateUtilizationPercentage(b.alloted, b.balance);
        return percentageB - percentageA;
      });
    });

    // Create HTML table with styles
    let htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #000; padding: 8px; text-align: center; }
            .title { background-color: #4472C4; color: white; font-weight: bold; font-size: 14px; }
            .header-0 { background-color: #CCE5FF; }
            .header-1 { background-color: #E5CCFF; }
            .header-2 { background-color: #CCFFCC; }
            .header-3 { background-color: #FFFF99; }
            .header-4 { background-color: #FFCCDD; }
            .header-5 { background-color: #CCDDFF; }
            .header-6 { background-color: #FFDDCC; }
            .header-7 { background-color: #FFCCCC; }
            .col-0 { background-color: #CCE5FF; }
            .col-1 { background-color: #E5CCFF; }
            .col-2 { background-color: #CCFFCC; }
            .col-3 { background-color: #FFFF99; }
            .col-4 { background-color: #FFCCDD; }
            .col-5 { background-color: #CCDDFF; }
            .col-6 { background-color: #FFDDCC; }
            .col-7 { background-color: #FFCCCC; }
          </style>
        </head>
        <body>
          <table>
            <tr>
              <td colspan="8" class="title">RRGSA - Center Wise Fund Utilization Report (2025-26)</td>
            </tr>
            <tr>
              <th class="header-0">Center Type</th>
              <th class="header-1">S.No</th>
              <th class="header-2">Center Name</th>
              <th class="header-3">Alloted Amount</th>
              <th class="header-4">Amount Utilised</th>
              <th class="header-5">No. of Work Code Generated</th>
              <th class="header-6">Percentage of Amount Utilized</th>
              <th class="header-7">Balance</th>
            </tr>
    `;

    // Add data rows
    Object.entries(groupedData).forEach(([centerType, centers]) => {
      centers.forEach((item, index) => {
        const allotedAmount = parseFloat(item.alloted) || 0;
        const balanceAmount = parseFloat(item.balance) || 0;
        const utilizedAmount = allotedAmount - balanceAmount;
        const utilizationPercentage = allotedAmount > 0 
          ? Math.round((utilizedAmount / allotedAmount) * 100) 
          : 0;
        
        const numCodes = item.workcode 
          ? item.workcode.replace(/;/g, ',').split(',').filter(code => code.trim()).length 
          : 0;

        htmlContent += `
          <tr>
            <td class="col-0">${centerType}</td>
            <td class="col-1">${index + 1}</td>
            <td class="col-2">${item.name || ''}</td>
            <td class="col-3">${Math.round(allotedAmount)}</td>
            <td class="col-4">${Math.round(utilizedAmount)}</td>
            <td class="col-5">${numCodes}</td>
            <td class="col-6">${utilizationPercentage}%</td>
            <td class="col-7">${Math.round(balanceAmount)}</td>
          </tr>
        `;
      });
    });

    htmlContent += `
          </table>
        </body>
      </html>
    `;

    // Create and download file
    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `All_Centers_Fund_Utilization_${new Date().toISOString().split('T')[0]}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Error exporting data. Please try again: ' + error.message);
  }
}

 // Share district data via WhatsApp
const shareViaWhatsApp = () => {
  try {
    setGeneratingPdf(true);
    
    // Filter only District centers
    const districtCenters = districtData.filter(item => item.center === 'District');
    
    if (districtCenters.length === 0) {
      alert('No district centers data available to export');
      setGeneratingPdf(false);
      return;
    }

    // Calculate totals
    const districtTotalWorks = districtCenters.reduce((total, item) => total + (parseInt(item.works) || 0), 0);
    const totalAlloted = districtCenters.reduce((sum, item) => sum + (item.alloted || 0), 0);
    const totalBalance = districtCenters.reduce((sum, item) => sum + (parseFloat(item.balance) || 0), 0);
    
    // Create PDF document (landscape orientation)
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Add title
    pdf.setFontSize(16);
    pdf.text('District-wise Utilised Report', pdf.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
    
    // Add date
    pdf.setFontSize(10);
    pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, pdf.internal.pageSize.getWidth() / 2, 22, { align: 'center' });
    
    // Add summary information
    pdf.setFontSize(12);
    pdf.text('Summary:', 14, 30);
    pdf.setFontSize(10);
    pdf.text(`Total Districts: ${districtCenters.length}`, 14, 36);
    pdf.text(`Total Works: ${districtTotalWorks}`, 14, 42);
    pdf.text(`Total Allotted: ₹${Math.round(totalAlloted).toLocaleString('en-IN')}`, 14, 48);
    pdf.text(`Total Balance: ₹${Math.round(totalBalance).toLocaleString('en-IN')}`, 14, 54);
    
    // Create table header
    const startY = 65;
const headers = ['S.No', 'District Name', 'Alloted Amount (₹)', 'No. of Works', 'Amount Utilised (₹)', 'Utilization %', 'Balance (₹)'];
const colWidths = [15, 40, 35, 25, 35, 25, 30];
    // Set up table styles
    pdf.setFillColor(220, 220, 220);
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.1);
    
    // Draw header row
    let currentX = 14;
    pdf.setFontSize(10);
    pdf.setTextColor(0);
    
    // Function to draw cell with text
    function drawCell(x, y, width, text, align = 'left') {
      pdf.rect(x, y, width, 8);
      
      // Calculate text position based on alignment
      let textX = x;
      if (align === 'center') {
        textX = x + width / 2;
      } else if (align === 'right') {
        textX = x + width - 2;
      } else {
        textX = x + 2;
      }
      
      pdf.text(String(text), textX, y + 5, { align: align });
    }
    
    // Draw header cells
    headers.forEach((header, i) => {
      pdf.setFillColor(220, 220, 220);
      pdf.rect(currentX, startY, colWidths[i], 8, 'FD');
      pdf.text(header, currentX + colWidths[i] / 2, startY + 5, { align: 'center' });
      currentX += colWidths[i];
    });
    
    // Sort districts by utilization percentage (highest first)
const sortedDistricts = [...districtCenters].sort((a, b) => {
  const percentageA = calculateUtilizationPercentage(a.alloted, a.balance);
  const percentageB = calculateUtilizationPercentage(b.alloted, b.balance);
  return percentageB - percentageA;
});
    
    // Draw data rows
    let rowY = startY + 8;
    
    sortedDistricts.forEach((district, index) => {
      // Check if we need a new page
      if (rowY > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        rowY = 20;
        
        // Add column headers to new page
        currentX = 14;
        headers.forEach((header, i) => {
          pdf.rect(currentX, rowY, colWidths[i], 8, 'FD');
          pdf.text(header, currentX + colWidths[i] / 2, rowY + 5, { align: 'center' });
          currentX += colWidths[i];
        });
        rowY += 8;
      }
      
      // Draw row
      currentX = 14;
      
      // S.No
      drawCell(currentX, rowY, colWidths[0], index + 1, 'center');
      currentX += colWidths[0];
      
      // District Name
      drawCell(currentX, rowY, colWidths[1], district.name, 'left');
      currentX += colWidths[1];
      
      // Alloted Amount - format as whole number
      drawCell(currentX, rowY, colWidths[2], Math.round(district.alloted).toLocaleString('en-IN'), 'right');
      currentX += colWidths[2];
   // No. of Works
// No. of Works
drawCell(currentX, rowY, colWidths[3], district.works, 'center');
currentX += colWidths[3];

// Amount Utilised
const utilizedAmount = Math.round(district.alloted - parseFloat(district.balance));
drawCell(currentX, rowY, colWidths[4], utilizedAmount.toLocaleString('en-IN'), 'right');
currentX += colWidths[4];

// Utilization Percentage
drawCell(currentX, rowY, colWidths[5], calculateUtilizationPercentage(district.alloted, district.balance) + '%', 'center');
currentX += colWidths[5];

// Balance - format as whole number
drawCell(currentX, rowY, colWidths[6], Math.round(parseFloat(district.balance)).toLocaleString('en-IN'), 'right');
      
      rowY += 8;
    });
    
    // Add total row
    currentX = 14;
    pdf.setFillColor(240, 240, 240);
    
    // S.No
    pdf.rect(currentX, rowY, colWidths[0], 8, 'FD');
    currentX += colWidths[0];
    
    // District Name
    pdf.rect(currentX, rowY, colWidths[1], 8, 'FD');
    pdf.text('TOTAL', currentX + 2, rowY + 5);
    currentX += colWidths[1];
    
    // Alloted Amount - format as whole number
    pdf.rect(currentX, rowY, colWidths[2], 8, 'FD');
    pdf.text(Math.round(totalAlloted).toLocaleString('en-IN'), currentX + colWidths[2] - 2, rowY + 5, { align: 'right' });
    currentX += colWidths[2];
    
// No. of Works
pdf.rect(currentX, rowY, colWidths[3], 8, 'FD');
pdf.text(String(districtTotalWorks), currentX + colWidths[3]/2, rowY + 5, { align: 'center' });
currentX += colWidths[3];

// Amount Utilised
const totalUtilised = Math.round(totalAlloted - totalBalance);
pdf.rect(currentX, rowY, colWidths[4], 8, 'FD');
pdf.text(totalUtilised.toLocaleString('en-IN'), currentX + colWidths[4] - 2, rowY + 5, { align: 'right' });
currentX += colWidths[4];

// Utilization Percentage
pdf.rect(currentX, rowY, colWidths[5], 8, 'FD');
// Leave this cell blank for totals
currentX += colWidths[5];

// Balance - format as whole number
pdf.rect(currentX, rowY, colWidths[6], 8, 'FD');
pdf.text(Math.round(totalBalance).toLocaleString('en-IN'), currentX + colWidths[6] - 2, rowY + 5, { align: 'right' });
    
    // Add footer
    pdf.setFontSize(8);
    pdf.text('Generated from District-wise Utilised Report Portal', pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 10, { align: 'center' });
    
    // Save the PDF with a filename
    pdf.save(`District_Wise_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF: ' + error.message);
  } finally {
    setGeneratingPdf(false);
  }
};

 // Calculate totals
 const calculateTotals = () => {
   return districtData.reduce((totals, item) => {
     return {
       alloted: totals.alloted + (item.alloted || 0),
       balance: totals.balance + (parseFloat(item.balance) || 0),
       works: totals.works + (parseInt(item.works) || 0)
     };
   }, { alloted: 0, balance: 0, works: 0 });
 };

 const totals = calculateTotals();

 // Calculate total works for District centers only
 const districtTotalWorks = districtData
   .filter(item => item.center === 'District')
   .reduce((total, item) => total + (parseInt(item.works) || 0), 0);

 // Calculate percentage for each district based on work codes (only for districts)
 // Updated to return whole numbers (no decimals)
 const calculatePercentage = (works, totalWorks) => {
   if (totalWorks === 0) return 0;
   return Math.round((parseInt(works) || 0) / totalWorks * 100);
 };

 // Group data by center type
 const groupedData = districtData.reduce((acc, item) => {
   const centerType = item.center || 'Unknown';
   if (!acc[centerType]) {
     acc[centerType] = [];
   }
   acc[centerType].push(item);
   return acc;
 }, {});

 Object.keys(groupedData).forEach(centerType => {
  groupedData[centerType].sort((a, b) => {
    const percentageA = calculateUtilizationPercentage(a.alloted, a.balance);
    const percentageB = calculateUtilizationPercentage(b.alloted, b.balance);
    return percentageB - percentageA; // Descending order
  });
});

 return (
   <div className="p-6">
     {/* Header with Back Button and Export Button */}
     <div className="flex justify-between items-center mb-6">
       <div className="flex items-center gap-4">
         <button            
           onClick={() => navigate('/report')}            
           className="flex items-center gap-2 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"          
         >            
           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">              
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />            
           </svg>            
           Back to Reports          
         </button>
         <h1 className="text-2xl font-bold">District-wise Utilised Report</h1>
       </div>
       <div className="flex gap-2">
         <button
           onClick={exportAllToExcel}
           className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
           disabled={loading || districtData.length === 0}
         >
           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
           </svg>
           Export All to Excel
         </button>
         <button
           onClick={fetchDistrictData}
           className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
         >
           Refresh Data
         </button>
       </div>
     </div>

     {/* Loading State */}
     {loading && (
       <div className="flex justify-center items-center h-64">
         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-blue-600"></div>
       </div>
     )}

     {/* Error State */}
     {error && (
       <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
         <p className="font-medium">Error: {error}</p>
         <button
           onClick={fetchDistrictData}
           className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
         >
           Try Again
         </button>
       </div>
     )}

     {/* Data Table */}
     {!loading && districtData.length > 0 && (
       <div className="bg-white rounded-lg shadow-sm border border-gray-200" ref={reportRef}>
         {Object.entries(groupedData).map(([centerType, centers]) => (
           <div key={centerType} className="mb-6" data-center-type={centerType}>
             <div className="flex justify-between items-center px-4 py-2 bg-gray-50 border-b border-gray-200">
               <h3 className="text-lg font-semibold text-gray-800">
                 {centerType} Centers ({centers.length})
               </h3>
             </div>
             <div className="overflow-x-auto">
               <table className="w-full border-collapse">
                 <thead className="bg-gray-50">
                   <tr>
                     <th className="border border-gray-300 px-4 py-3 text-left text-sm font-medium text-gray-900">S.No</th>
                     <th className="border border-gray-300 px-4 py-3 text-left text-sm font-medium text-gray-900">Center Name</th>
                     <th className="border border-gray-300 px-4 py-3 text-left text-sm font-medium text-gray-900">Type</th>
                     <th className="border border-gray-300 px-4 py-3 text-right text-sm font-medium text-gray-900">Alloted Amount</th>
                 <th className="border border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-900">No. of Works</th>
<th className="border border-gray-300 px-4 py-3 text-right text-sm font-medium text-gray-900">Amount Utilised</th>
<th className="border border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-900">Percentage of Amount Utilized</th>
                     <th className="border border-gray-300 px-4 py-3 text-right text-sm font-medium text-gray-900">Balance</th>
                     <th className="border border-gray-300 px-4 py-3 text-left text-sm font-medium text-gray-900">Work Codes</th>
                   </tr>
                 </thead>
                 <tbody>
                   {centers.map((item, index) => {
                     const balance = parseFloat(item.balance);
                  const utilizationPercentage = calculateUtilizationPercentage(item.alloted, item.balance);
                     
                     return (
                       <tr key={`${item.type}-${index}`} className="hover:bg-gray-50">
                         <td className="border border-gray-300 px-4 py-3 text-sm">{index + 1}</td>
                         <td className="border border-gray-300 px-4 py-3">
                           <div className="font-medium text-gray-900">{item.name}</div>
                           <div className="text-xs text-gray-500 uppercase">{item.type}</div>
                         </td>
                         <td className="border border-gray-300 px-4 py-3">
                           <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                             centerType === 'State' ? 'bg-purple-100 text-purple-800' :
                             centerType === 'Regional' ? 'bg-blue-100 text-blue-800' :
                             'bg-green-100 text-green-800'
                           }`}>
                             {centerType}
                           </span>
                         </td>
                         <td className="border border-gray-300 px-4 py-3 text-right font-mono">
                           <span className="text-gray-900 font-medium">
                             ₹{Math.round(item.alloted).toLocaleString('en-IN')}
                           </span>
                         </td>
             <td className="border border-gray-300 px-4 py-3 text-center">
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    parseInt(item.works) > 5 ? 'bg-blue-100 text-blue-800' :
    parseInt(item.works) > 0 ? 'bg-green-100 text-green-800' :
    'bg-gray-100 text-gray-800'
  }`}>
    {item.works}
  </span>
</td>
<td className="border border-gray-300 px-4 py-3 text-right font-mono">
  <span className="text-gray-900 font-medium">
    ₹{Math.round(item.alloted - parseFloat(item.balance)).toLocaleString('en-IN')}
  </span>
</td>
<td className="border border-gray-300 px-4 py-3 text-center">
  <div className="flex items-center justify-center">
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      utilizationPercentage >= 10 ? 'bg-green-100 text-green-800' :
      utilizationPercentage >= 5 ? 'bg-yellow-100 text-yellow-800' :
      utilizationPercentage > 0 ? 'bg-orange-100 text-orange-800' :
      'bg-red-100 text-red-800'
    }`}>
      {utilizationPercentage}%
    </span>
  </div>
</td>
                         <td className="border border-gray-300 px-4 py-3 text-right font-mono">
                           <span className={`font-medium ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                             ₹{Math.round(balance).toLocaleString('en-IN')}
                           </span>
                         </td>
                         <td className="border border-gray-300 px-4 py-3">
                           {item.workcode ? (
                             <div className="max-w-xs">
                               <details className="cursor-pointer">
                                 <summary className="text-sm text-blue-600 hover:text-blue-800">
                                   View Work Codes ({item.works})
                                 </summary>
                                 <div className="mt-2 p-3 bg-gray-50 rounded text-xs">
                                   {cleanWorkCodes(item.workcode).split(', ').map((code, idx) => (
                                     <div key={idx} className="mb-1 p-1 bg-white rounded font-mono text-xs">
                                       {code.trim()}
                                     </div>
                                   ))}
                                 </div>
                               </details>
                             </div>
                           ) : (
                             <span className="text-gray-400 italic text-sm">No work codes</span>
                           )}
                         </td>
                       </tr>
                     );
                   })}
                 </tbody>
               </table>
             </div>
           </div>
         ))}
       </div>
     )}

     {/* No Data State */}
     {!loading && districtData.length === 0 && !error && (
       <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
         <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
         </svg>
         <p className="mt-2 text-gray-600">No district-wise data available</p>
         <button 
           onClick={fetchDistrictData}
           className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
         >
           Fetch District Data
         </button>
       </div>
     )}
   </div>
 );
};

export default DistrictWiseReports;